import React, { useEffect, useState } from 'react';
import './MayoralDecisions.css';
import apiClient from '../lib/api';

type MayoralBylaw = {
  id: number;
  bylaw_number: string;
  item_number?: string;
  description?: string;
  bylaw_url?: string;
};

type MayoralDecision = {
  id: number;
  decision_id: string;
  municipality: string;
  title: string;
  decision_date: string;
  decision_date_text?: string;
  url: string;
  pdf_path?: string;
  bylaw_count: number;
  year?: number;
  bylaws: MayoralBylaw[];
};

const MayoralDecisions: React.FC = () => {
  const [decisions, setDecisions] = useState<MayoralDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchDecisions();
  }, []);

  const fetchDecisions = async () => {
    try {
      setLoading(true);
      const data = await apiClient.mayoralDecisions.list();
      setDecisions(data);
      setError(null);
    } catch (e: any) {
      console.error('Failed to load mayoral decisions:', e);
      setError('Failed to load mayoral decisions');
    } finally {
      setLoading(false);
    }
  };

  const toggleBylaws = (decisionId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(decisionId)) {
      newExpanded.delete(decisionId);
    } else {
      newExpanded.add(decisionId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="mayoral-page">
        <div className="page-header">
          <h1 className="page-title">Mayoral Decisions</h1>
          <p className="page-subtitle">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mayoral-page">
        <div className="page-header">
          <h1 className="page-title">Mayoral Decisions</h1>
          <p className="page-subtitle error-text">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mayoral-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">Mayoral Decisions</h1>
            <p className="page-subtitle">
              Complete list of decisions and approved bylaws
            </p>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="decisions-table">
          <thead>
            <tr>
              <th>Decision ID</th>
              <th>Date</th>
              <th>Bylaws</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {decisions.map((decision) => (
              <React.Fragment key={decision.id}>
                <tr className="decision-row">
                  <td>
                    <div className="decision-id">{decision.decision_id}</div>
                    {decision.title && (
                      <div className="meeting-type">
                        {decision.title}
                      </div>
                    )}
                  </td>
                  <td className="date-cell">
                    {decision.decision_date ? formatDate(decision.decision_date) : decision.decision_date_text || 'N/A'}
                  </td>
                  <td>
                    <span className="bylaw-count">
                      {decision.bylaw_count} bylaw{decision.bylaw_count !== 1 ? 's' : ''}
                    </span>
                    {decision.bylaw_count > 0 && (
                      <>
                        <br />
                        <span 
                          className="bylaws-toggle" 
                          onClick={() => toggleBylaws(decision.id)}
                        >
                          {expandedRows.has(decision.id) ? 'Hide details ▴' : 'View details ▾'}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    {decision.url && (
                      <a 
                        href={decision.url} 
                        className="view-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View PDF
                      </a>
                    )}
                  </td>
                </tr>
                {expandedRows.has(decision.id) && decision.bylaws.length > 0 && (
                  <tr className="bylaws-row">
                    <td colSpan={4}>
                      <div className="bylaws-detail">
                        {decision.bylaws.map((bylaw) => (
                          <div key={bylaw.id} className="bylaw-row">
                            <div className="bylaw-header">
                              <span className="bylaw-num">{bylaw.bylaw_number}</span>
                              {bylaw.item_number && (
                                <span className="item-num">({bylaw.item_number})</span>
                              )}
                            </div>
                            {bylaw.description && (
                              <div className="bylaw-desc">
                                {bylaw.description}
                              </div>
                            )}
                            {bylaw.bylaw_url && (
                              <a 
                                href={bylaw.bylaw_url} 
                                className="bylaw-link"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View bylaw document →
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        {decisions.length === 0 && (
          <div className="empty-state">
            <p>No mayoral decisions found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MayoralDecisions;

